import { ClientPaginatedTable } from '@avanta/shared/components/ClientPaginatedTable';
import { Button } from '@avanta/shared/components/ui/Button';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { t } from '@avanta/shared/i18n';
import { type TableColumnFilterOption } from '@avanta/shared/types/table';
import { getStatusBadge } from '@avanta/shared/utils/appointments';
import { type ColumnDef } from '@tanstack/react-table';
import { CalendarCog, CalendarDays, ChevronRight } from 'lucide-react';
import { useCallback, useMemo, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router';
import {
  ENTERPRISE_APPOINTMENT_FILTER,
  UPCOMING_APPOINTMENT_STATUSES,
} from 'constants/appointments';
import { ROUTES } from 'constants/routes';
import { useFetchEnterpriseAppointments } from 'hooks/useFetchEnterpriseAppointments';
import { type AppointmentWithDetails } from 'types/appointments';
import {
  getSessionStatusColumnFilter,
  getSpecialityFilterOptions,
  getSpecialityLabel,
} from 'utils/appointments';
import { formatRelativeDateTime } from 'utils/date';

const getColumns = (
  specialityFilterOptions: TableColumnFilterOption[],
  handleOnManageAppointment: (appointment: AppointmentWithDetails) => void
): ColumnDef<AppointmentWithDetails>[] => [
  {
    id: 'dateTime',
    accessorKey: 'dateTime',
    enableSorting: true,
    header: t('AppointmentsTable.date.label', { ns: LOCIZE_NS.APPOINTMENTS }),
    cell: ({ row }): string => formatRelativeDateTime(row.original.dateTime),
  },
  {
    id: 'specialitiesBookable',
    accessorKey: 'specialitiesBookable',
    header: t('AppointmentPdf.Speciality.heading', { ns: LOCIZE_NS.APPOINTMENTS }),
    meta: {
      columnFilter: {
        options: specialityFilterOptions,
      },
    },
    cell: ({ row }): string => getSpecialityLabel(row.original.specialitiesBookable),
  },
  {
    id: 'patient',
    accessorKey: 'patientName',
    enableSorting: true,
    header: t('InsuranceAppointmentsList.patient.label', { ns: LOCIZE_NS.INSURANCE }),
    meta: {
      truncation: {
        maxWidthClassName: 'max-w-22',
      },
    },
    cell: ({ row }): ReactElement => (
      <span className="block truncate">{row.original.patientName}</span>
    ),
  },
  {
    id: 'status',
    header: t('AppointmentsTable.status.label', { ns: LOCIZE_NS.APPOINTMENTS }),
    accessorKey: 'status',
    enableColumnFilter: true,
    meta: {
      columnFilter: getSessionStatusColumnFilter(UPCOMING_APPOINTMENT_STATUSES),
    },
    cell: ({ row }): ReactElement => getStatusBadge(row.original),
  },
  {
    id: 'actions',
    header: t('AppointmentsTable.actions.label', { ns: LOCIZE_NS.APPOINTMENTS }),
    cell: ({ row }): ReactElement => (
      <Button
        variant="terciary"
        icon="leading"
        onClick={() => handleOnManageAppointment(row.original)}
      >
        <CalendarCog className="size-5" />
        {t('SessionsPage.actions.manageSession', { ns: LOCIZE_NS.INSURANCE })}
      </Button>
    ),
  },
];

export const NextAppointments = (): ReactElement => {
  const { t } = useTranslation(LOCIZE_NS.INSURANCE);
  const navigate = useNavigate();
  const { data: appointments, isLoading } = useFetchEnterpriseAppointments({
    limit: 5,
    offset: 0,
    status: ENTERPRISE_APPOINTMENT_FILTER.UPCOMING,
  });

  const handleOnManageAppointment = useCallback(
    (appointment: AppointmentWithDetails): void => {
      navigate(generatePath(ROUTES.HOME_APPOINTMENT_DETAIL, { id: appointment.patientId }), {
        state: {
          patientId: appointment.patientId,
          appointment,
        },
      });
    },
    [navigate]
  );

  const columns = useMemo(
    () => getColumns(getSpecialityFilterOptions(appointments ?? []), handleOnManageAppointment),
    [appointments, handleOnManageAppointment]
  );

  const actionSlot = appointments?.length ? (
    <Button variant="link" icon="leading" onClick={() => navigate(ROUTES.APPOINTMENTS)}>
      {t('HomePage.viewAll')}
      <ChevronRight className="size-4" />
    </Button>
  ) : undefined;

  return (
    <div className="flex h-fit w-full flex-col gap-4">
      <ClientPaginatedTable
        title={t('HomePage.recentSessions.heading')}
        icon={CalendarDays}
        data={appointments ?? []}
        columns={columns}
        isLoading={isLoading}
        emptyTitle={t('HomePage.recentSessions.empty')}
        tableClassName="w-full"
        pageSize={5}
        autoPageSize
        onRowAction={row => handleOnManageAppointment(row)}
        actionSlot={actionSlot}
      />
    </div>
  );
};
